import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Footer from './components/common/Footer';
import AuthGate from './components/common/AuthGate';
import PublicLayout from './components/common/PublicLayout';
import LandingPage from './components/common/LandingPage';
import HowToUsePage from './components/common/HowToUsePage';
import PrivacyPolicyPage from './components/common/PrivacyPolicyPage';
import UserSettings from './components/common/UserSettings';
import ContactSupportPage from './components/common/ContactSupportPage';
import AdminPage from './components/common/AdminPage';
import AccountDetailPage from './components/common/AccountDetailPage';
import StaffPage from './components/common/StaffPage';
import ChatWidget from './components/common/ChatWidget';
import UpgradeModal from './components/common/UpgradeModal';
import MainDashboard from './components/dashboard/MainDashboard';
import ExpenseDashboard from './components/expense/ExpenseDashboard';
import GoalDashboard from './components/goals/GoalDashboard';
import ChecklistDashboard from './components/checklist/ChecklistDashboard';
import { ExpenseProvider } from './contexts/ExpenseContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { GoalProvider } from './contexts/GoalContext';
import { ChecklistProvider } from './contexts/ChecklistContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { CharacterProvider } from './contexts/CharacterContext';
import { ThemeProvider } from './hooks/useTheme';
import { CurrencyProvider } from './hooks/useCurrency';
import { AuthProvider, useAuth } from './hooks/useAuth';

function RequireRole({ allowed, role, children }) {
  if (!allowed.includes(role)) return <Navigate to="/" replace />;
  return children;
}

function UserOnlyRoute({ role, children }) {
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role === 'staff') return <Navigate to="/staff" replace />;
  return children;
}

function AppContent() {
  const { isAuthenticated, isReady, role } = useAuth();
  const { t } = useTranslation('common');

  if (!isReady) {
    return (
      <div className="app-loading">
        <div className="loader" />
        <p>{t('loadingApp')}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/how-to-use" element={<HowToUsePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
        </Route>
        <Route path="/login" element={<AuthGate initialMode="login" />} />
        <Route path="/signup" element={<AuthGate initialMode="signup" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-shell">
      <Header />
      <div className="app-layout">
        <Sidebar />
        <main className="content-area">
          <Routes>
            <Route path="/" element={<UserOnlyRoute role={role}><MainDashboard /></UserOnlyRoute>} />
            <Route path="/expenses" element={<UserOnlyRoute role={role}><ExpenseDashboard /></UserOnlyRoute>} />
            <Route path="/goals" element={<UserOnlyRoute role={role}><GoalDashboard /></UserOnlyRoute>} />
            <Route path="/checklist" element={<UserOnlyRoute role={role}><ChecklistDashboard /></UserOnlyRoute>} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/support" element={<ContactSupportPage />} />
            <Route path="/how-to-use" element={<HowToUsePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/staff" element={<RequireRole allowed={['staff']} role={role}><StaffPage /></RequireRole>} />
            <Route path="/staff/users/:id" element={<RequireRole allowed={['staff']} role={role}><AccountDetailPage /></RequireRole>} />
            <Route path="/admin" element={<RequireRole allowed={['admin']} role={role}><AdminPage /></RequireRole>} />
            <Route path="/admin/accounts/:id" element={<RequireRole allowed={['admin']} role={role}><AccountDetailPage /></RequireRole>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <Footer />
      <ChatWidget />
      <UpgradeModal />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <CurrencyProvider>
          <ToastProvider>
            <ConfirmProvider>
              <CharacterProvider>
                <SubscriptionProvider>
                  <ExpenseProvider>
                    <ChecklistProvider>
                      <GoalProvider>
                        <BrowserRouter>
                          <AppContent />
                        </BrowserRouter>
                      </GoalProvider>
                    </ChecklistProvider>
                  </ExpenseProvider>
                </SubscriptionProvider>
              </CharacterProvider>
            </ConfirmProvider>
          </ToastProvider>
        </CurrencyProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
